import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'redux-form';
import arrayMove from 'array-move';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import { TextInput, SelectInput, StatusChip, SearchClear } from '../../_Components';

export const SYSTEM_FIELDS = [
    { value: 'result', label: 'Результат' },
    { value: 'task', label: 'Тема' },
    { value: 'sub_task', label: 'Подтема' },
];

export const STATUS_OPTIONS = [
    { id: 'Active', name: 'Активен' },
    { id: 'Disabled', name: 'Отключён' },
];

export const formatReportStatus = (value) => (value === 'Diasbled' ? 'Disabled' : value);

export const TARIF_OPTIONS = [
    { id: 'seconds', name: 'Посекундная' },
    { id: 'minutes', name: 'Поминутная' },
];

export const METRICS = [
    { name: 'sl', title: 'Service Level', hint: 'Доля звонков, принятых в пороговое время' },
    { name: 'abandoned', title: 'Потерянные', hint: 'Сколько клиентов не дождались ответа' },
    { name: 'connected', title: 'Отвеченные', hint: 'Сколько звонков принято операторами' },
    { name: 'talktime', title: 'Время разговора', hint: 'Суммарная и средняя длительность' },
    { name: 'held', title: 'Время удержания', hint: 'Сколько клиент провёл на удержании' },
    { name: 'waiting', title: 'Время ожидания', hint: 'Сколько ждал до ответа оператора' },
];

export const STATS = [
    { name: 'result', title: 'По результату', hint: 'Диаграмма по справочнику «Результат»' },
    { name: 'task', title: 'По теме', hint: 'Диаграмма по справочнику «Тема»' },
    { name: 'trasfer', title: 'По переводам', hint: 'Куда переводились звонки' },
];

const SAMPLE = {
    waiting: '00:12',
    timedate: '25.08.2026 07:01',
    phone: '+375 44 558-36-98',
    last_name: 'Ковалёв',
    first_name: 'Дмитрий',
    second_name: 'Сергеевич',
    full_name: 'Ковалёв Дмитрий',
    organization: 'ООО «Автолига»',
    department: 'Сервис',
    trasfer_success: 'Успешно',
    trasfer_number: '471',
    task: 'NISSAN: Сервис',
    task_value: 'NISSAN: Сервис',
    sub_task: 'Запись на ТО',
    subtopic: 'Запись на ТО',
    result: 'Обработан полностью',
    description: 'Записан на 26.08',
    operator: 'op014',
    duration: '00:02:49',
    wait_time: '00:12',
    hold_time: '00:00:31',
    queue: 'Nissan — ДрайвМоторс',
};

const isMonoSample = (value) => /^[\d:+.\s—-]+$/.test(value);

export const Icon = ({ d, size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const Toggle = ({ input, disabled }) => (
    <label className="pc-pr-tog">
        <input
            type="checkbox"
            checked={!!input.value}
            disabled={disabled}
            onChange={(event) => input.onChange(event.target.checked)}
        />
        <span className="tr" />
    </label>
);

export const ToggleRow = ({ name, title, hint, extra, disabled }) => (
    <div className="pc-pr-trow">
        <div className="pc-pr-trow__txt">
            <b>{title}</b>
            {hint ? <span>{hint}</span> : null}
        </div>
        {extra}
        <Field name={name} component={Toggle} disabled={disabled} />
    </div>
);

const queueId = (queue) => (queue && (queue.id || queue._id)) || '';

const isOutboundQueue = (queue) => {
    if (!queue) return false;
    const type = String(queue.type || queue.direction || '').toLowerCase();
    return type === 'outbound' || type === 'out';
};

export const emptyQueues = () => ({ inbounds: [], outbounds: [] });

export const normalizeQueues = (value) => ({
    inbounds: Array.isArray(value && value.inbounds) ? value.inbounds.slice() : [],
    outbounds: Array.isArray(value && value.outbounds) ? value.outbounds.slice() : [],
});

export const QueuesPicker = ({ input, queuesList = [], disabled }) => {
    const [query, setQuery] = useState('');
    const wrapRef = useRef(null);
    const queues = normalizeQueues(input.value);
    const selectedIds = new Set([].concat(queues.inbounds, queues.outbounds).map(String));
    const all = queuesList || [];
    const selected = [].concat(
        queues.inbounds.map((id) => ({ id, dir: 'in', queue: all.find((item) => String(queueId(item)) === String(id)) })),
        queues.outbounds.map((id) => ({ id, dir: 'out', queue: all.find((item) => String(queueId(item)) === String(id)) }))
    );
    const q = query.toLowerCase().trim();
    const matches = q
        ? all.filter((item) => {
            const id = String(queueId(item));
            if (!id || selectedIds.has(id)) return false;
            return String(item.name || '').toLowerCase().includes(q);
        })
        : [];

    useEffect(() => {
        const onDoc = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setQuery('');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const write = (next) => input.onChange(next);

    const addQueue = (item) => {
        const id = queueId(item);
        if (!id || selectedIds.has(String(id))) return;
        const next = normalizeQueues(input.value);
        if (isOutboundQueue(item)) next.outbounds.unshift(id);
        else next.inbounds.unshift(id);
        write(next);
        setQuery('');
    };

    const removeQueue = (id) => {
        const next = normalizeQueues(input.value);
        next.inbounds = next.inbounds.filter((item) => String(item) !== String(id));
        next.outbounds = next.outbounds.filter((item) => String(item) !== String(id));
        write(next);
    };

    const addAllInbound = () => {
        const next = normalizeQueues(input.value);
        all.forEach((item) => {
            if (isOutboundQueue(item)) return;
            const id = queueId(item);
            if (!id) return;
            const taken = next.inbounds.some((itemId) => String(itemId) === String(id))
                || next.outbounds.some((itemId) => String(itemId) === String(id));
            if (!taken) next.inbounds.push(id);
        });
        write(next);
    };

    return (
        <div>
            <div className="pc-pr-qadd">
                <div className="pc-pr-srch" ref={wrapRef}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <circle cx="7" cy="7" r="4.5" />
                        <path d="M10.5 10.5L14 14" />
                    </svg>
                    <input
                        className="pc-pr-inp"
                        value={query}
                        disabled={disabled}
                        placeholder="Найти очередь и добавить"
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <SearchClear visible={Boolean(query)} onClear={() => setQuery('')} disabled={disabled} />
                    {q ? (
                        <div className="pc-pr-drop">
                            {matches.length
                                ? matches.map((item) => (
                                    <button
                                        type="button"
                                        key={queueId(item)}
                                        onClick={() => addQueue(item)}
                                    >
                                        <span>{item.name}</span>
                                        <i>{isOutboundQueue(item) ? 'исходящая' : 'входящая'}</i>
                                    </button>
                                ))
                                : <div className="pc-pr-empty">Ничего не найдено</div>}
                        </div>
                    ) : null}
                </div>
                <button type="button" className="pc-pr-btn pc-pr-btn--sm" disabled={disabled} onClick={addAllInbound}>
                    Все входящие
                </button>
            </div>
            <div className="pc-pr-qlist">
                {selected.length ? selected.map((row) => (
                    <div className="pc-pr-qrow" key={`${row.dir}-${row.id}`}>
                        <span className="nm">{(row.queue && row.queue.name) || row.id}</span>
                        <i>{row.dir === 'out' ? 'исходящая' : 'входящая'}</i>
                        <button type="button" className="rm" disabled={disabled} onClick={() => removeQueue(row.id)} aria-label="Убрать очередь">
                            <Icon size={13} d="M4 4l8 8M12 4l-8 8" />
                        </button>
                    </div>
                )) : (
                    <div className="pc-pr-empty">Очереди не выбраны — отчёт будет пустым.</div>
                )}
            </div>
            <div className="pc-pr-legrow">
                <span><span className="pc-pr-arr in"><Icon size={11} d="M11 5L5 11M5 6.5V11h4.5" /></span>входящая</span>
                <span><span className="pc-pr-arr out"><Icon size={11} d="M5 11l6-6M6.5 5H11v4.5" /></span>исходящая (обзвон)</span>
            </div>
        </div>
    );
};

const Grab = SortableHandle(() => (
    <span className="grab" aria-label="Перетащить" onClick={(event) => event.stopPropagation()}>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="6" cy="4" r="1.1" /><circle cx="10" cy="4" r="1.1" />
            <circle cx="6" cy="8" r="1.1" /><circle cx="10" cy="8" r="1.1" />
            <circle cx="6" cy="12" r="1.1" /><circle cx="10" cy="12" r="1.1" />
        </svg>
    </span>
));

const ColumnRow = ({ item, indexLabel, checked, onToggle, grab }) => (
    <div
        className={`pc-pr-it${checked ? ' is-sel' : ''}`}
        onClick={() => onToggle(item.value)}
    >
        <input type="checkbox" readOnly checked={checked} />
        <span className="ix">{indexLabel}</span>
        <span className="nm">{item.label || item.value}</span>
        {grab ? <Grab /> : <span className="grab" />}
    </div>
);

const SortableColumnRow = SortableElement((props) => <ColumnRow {...props} grab />);

const SortableColumnList = SortableContainer(({ items, selB, onToggle }) => (
    <div className="pc-pr-clist">
        {items.map((item, index) => (
            <SortableColumnRow
                key={item.value}
                index={index}
                item={item}
                indexLabel={index + 1}
                checked={selB.has(item.value)}
                onToggle={onToggle}
            />
        ))}
    </div>
));

export const ColumnPicker = ({ input, available = [], kind, cardName, onCopyFromScreen }) => {
    const [queryA, setQueryA] = useState('');
    const [queryB, setQueryB] = useState('');
    const [selA, setSelA] = useState(() => new Set());
    const [selB, setSelB] = useState(() => new Set());
    const selected = Array.isArray(input.value) ? input.value : [];
    const selectedKeys = new Set(selected.map((item) => item && item.value));
    const left = (available || []).filter((item) => item && item.value && !selectedKeys.has(item.value));
    const qa = queryA.toLowerCase().trim();
    const qb = queryB.toLowerCase().trim();
    const fa = left.filter((item) => !qa || `${item.label || ''} ${item.value}`.toLowerCase().includes(qa));
    const fb = selected.filter((item) => !qb || `${item.label || ''} ${item.value}`.toLowerCase().includes(qb));

    const toggleSet = (setter, key) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const addSelected = () => {
        const moving = left.filter((item) => selA.has(item.value));
        if (!moving.length) return;
        input.onChange(selected.concat(moving));
        setSelA(new Set());
    };

    const removeSelected = () => {
        if (!selB.size) return;
        input.onChange(selected.filter((item) => !selB.has(item.value)));
        setSelB(new Set());
    };

    const toggleAll = (list, set, current) => {
        const keys = list.map((item) => item.value);
        const allOn = keys.length && keys.every((key) => current.has(key));
        set(allOn ? new Set() : new Set(keys));
    };

    const canSort = !qb;

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex) return;
        input.onChange(arrayMove(selected, oldIndex, newIndex));
    };

    return (
        <div>
            <div className="pc-pr-card">
                <div className="ch">
                    <h3>{kind === 'xls' ? 'Колонки файла Excel' : 'Колонки таблицы на экране'}</h3>
                    <span className="hint">порядок в правом списке = порядок колонок</span>
                    {kind === 'xls' && onCopyFromScreen ? (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
                            <button type="button" className="pc-pr-btn pc-pr-btn--sm" onClick={onCopyFromScreen}>
                                <Icon size={14} d="M5 5h9v9H5zM11 5V3.2A1.2 1.2 0 009.8 2H3.2A1.2 1.2 0 002 3.2v6.6A1.2 1.2 0 003.2 11H5" />
                                Взять состав с экрана
                            </button>
                        </div>
                    ) : null}
                </div>
                <div className="cb">
                    <div className="pc-pr-dual">
                        <div className="pc-pr-col">
                            <div className="pc-pr-colh">
                                <span className="t">Доступные поля</span>
                                <span className="n">{fa.length}{selA.size ? ` · выбрано ${selA.size}` : ''}</span>
                            </div>
                            <div className="pc-pr-cols">
                                <div className="pc-pr-srch">
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                                        <circle cx="7" cy="7" r="4.5" />
                                        <path d="M10.5 10.5L14 14" />
                                    </svg>
                                    <input className="pc-pr-inp" value={queryA} placeholder="Поиск" onChange={(e) => setQueryA(e.target.value)} />
                                    <SearchClear visible={Boolean(queryA)} onClear={() => setQueryA('')} />
                                </div>
                                <button type="button" className="pc-pr-btn pc-pr-btn--sm" onClick={() => toggleAll(fa, setSelA, selA)}>Все</button>
                            </div>
                            <div className="pc-pr-clist">
                                {fa.length ? fa.map((item) => (
                                    <button
                                        type="button"
                                        key={item.value}
                                        className={`pc-pr-it${selA.has(item.value) ? ' is-sel' : ''}`}
                                        onClick={() => toggleSet(setSelA, item.value)}
                                    >
                                        <input type="checkbox" readOnly checked={selA.has(item.value)} />
                                        <span className="nm">{item.label || item.value}</span>
                                        <span className="k">{item.value}</span>
                                    </button>
                                )) : <div className="pc-pr-empty">Все поля уже добавлены</div>}
                            </div>
                        </div>
                        <div className="pc-pr-mid">
                            <button type="button" disabled={!selA.size} onClick={addSelected} aria-label="Добавить в отчёт">
                                <Icon d="M3 8h9M9 4.5L12.5 8 9 11.5" />
                            </button>
                            <button type="button" disabled={!selB.size} onClick={removeSelected} aria-label="Убрать из отчёта">
                                <Icon d="M13 8H4M7 4.5L3.5 8 7 11.5" />
                            </button>
                        </div>
                        <div className="pc-pr-col">
                            <div className="pc-pr-colh">
                                <span className="t">Колонки отчёта</span>
                                <span className="n">{selected.length}{selB.size ? ` · выбрано ${selB.size}` : ''}</span>
                            </div>
                            <div className="pc-pr-cols">
                                <div className="pc-pr-srch">
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                                        <circle cx="7" cy="7" r="4.5" />
                                        <path d="M10.5 10.5L14 14" />
                                    </svg>
                                    <input className="pc-pr-inp" value={queryB} placeholder="Поиск" onChange={(e) => setQueryB(e.target.value)} />
                                    <SearchClear visible={Boolean(queryB)} onClear={() => setQueryB('')} />
                                </div>
                                <button type="button" className="pc-pr-btn pc-pr-btn--sm" onClick={() => toggleAll(fb, setSelB, selB)}>Все</button>
                            </div>
                            {fb.length ? (
                                canSort ? (
                                    <SortableColumnList
                                        items={fb}
                                        selB={selB}
                                        onToggle={(value) => toggleSet(setSelB, value)}
                                        useDragHandle
                                        axis="y"
                                        lockAxis="y"
                                        helperClass="pc-pr-it is-drag"
                                        onSortEnd={onSortEnd}
                                    />
                                ) : (
                                    <div className="pc-pr-clist">
                                        {fb.map((item) => (
                                            <ColumnRow
                                                key={item.value}
                                                item={item}
                                                indexLabel={selected.findIndex((col) => col.value === item.value) + 1}
                                                checked={selB.has(item.value)}
                                                onToggle={(value) => toggleSet(setSelB, value)}
                                            />
                                        ))}
                                    </div>
                                )
                            ) : <div className="pc-pr-empty">Колонки не выбраны</div>}
                        </div>
                    </div>

                    <div className="pc-pr-prev">
                        <div className="pc-pr-prevh">{kind === 'xls' ? 'Как увидит клиент в файле' : 'Как увидит клиент'}</div>
                        <div className="pc-pr-tblwrap">
                            <table>
                                <thead>
                                    <tr>
                                        {(selected.length ? selected : [{ label: '—' }]).map((item, index) => (
                                            <th key={item.value || index}>{item.label || item.value || '—'}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[0, 1].map((row) => (
                                        <tr key={row}>
                                            {(selected.length ? selected : [{ value: '' }]).map((item, index) => {
                                                const value = SAMPLE[item.value] || '—';
                                                return (
                                                    <td key={item.value || index} className={isMonoSample(value) ? 'm' : undefined}>
                                                        {value}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="pc-pr-note">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="8" cy="8" r="6" />
                            <path d="M8 7.5v4M8 5.2v.6" />
                        </svg>
                        <span>
                            Список полей берётся из карточки{cardName ? <> <b>«{cardName}»</b></> : ''}. Если поля нет — добавьте его во вкладке «Поля» проекта.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const mergeReportFields = (projectFields) => {
    const extras = ((projectFields) || [])
        .filter((field) => field && field.value)
        .map((field) => ({ value: field.value, label: field.label || field.value }));
    const seen = new Set(SYSTEM_FIELDS.map((item) => item.value));
    return SYSTEM_FIELDS.concat(extras.filter((field) => {
        if (seen.has(field.value)) return false;
        seen.add(field.value);
        return true;
    }));
};

export const ReportSettingsFields = ({
    prefix = '',
    report,
    campaigns,
    queuesList,
    change,
    isSaving,
    showCampaign = true,
    extraMain,
}) => {
    const nameOf = (path) => `${prefix}${path}`;
    const outbound = !!(report && report.settings && report.settings.outbound);
    const queues = normalizeQueues(report && report.settings && report.settings.queues);
    const queueCount = queues.inbounds.length + queues.outbounds.length;

    return (
        <div className="pc-pr-wrap">
            <div className="pc-pr-card">
                <div className="ch"><h3>Основное</h3></div>
                <div className="cb">
                    <div className="pc-pr-fgrid">
                        <Field name={nameOf('name')} label="Название" required component={TextInput} disabled={isSaving} />
                        <Field
                            name={nameOf('status')}
                            label="Статус"
                            options={STATUS_OPTIONS}
                            component={SelectInput}
                            format={formatReportStatus}
                            normalize={formatReportStatus}
                            disabled={isSaving}
                        />
                        {showCampaign ? (
                            <div>
                                <Field
                                    name={nameOf('settings.campaign')}
                                    label="Карточка"
                                    required
                                    options={campaigns || []}
                                    component={SelectInput}
                                    disabled={isSaving}
                                />
                                <div className="pc-field__hint">Из неё берутся поля для колонок.</div>
                            </div>
                        ) : extraMain}
                        <Field
                            name={nameOf('settings.tarif')}
                            label="Тарификация"
                            options={TARIF_OPTIONS}
                            component={SelectInput}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </div>

            <div className="pc-pr-card">
                <div className="ch">
                    <h3>Что доступно клиенту в кабинете</h3>
                    <span className="hint">права на действия внутри отчёта</span>
                </div>
                <div className="cb">
                    <div className="pc-pr-tgrid">
                        <ToggleRow
                            name={nameOf('settings.outbound')}
                            title="Исходящие звонки"
                            hint="Показывать наравне с входящими"
                            disabled={isSaving}
                            extra={outbound ? (
                                <div className="pc-pr-prefix">
                                    <Field name={nameOf('settings.prefix')} component={TextInput} placeholder="Префикс" disabled={isSaving} />
                                </div>
                            ) : null}
                        />
                        <ToggleRow name={nameOf('settings.visor')} title="Прослушивание записей" hint="Плеер прямо в строке отчёта" disabled={isSaving} />
                        <ToggleRow name={nameOf('settings.export')} title="Экспорт в Excel" hint="Кнопка выгрузки таблицы" disabled={isSaving} />
                        <ToggleRow name={nameOf('settings.download')} title="Скачивание записей" hint="Сохранение mp3 на диск" disabled={isSaving} />
                    </div>
                </div>
            </div>

            <div className="pc-pr-card">
                <div className="ch">
                    <h3>Показатели звонков</h3>
                    <span className="hint">сводка над таблицей</span>
                    <div style={{ marginLeft: 'auto' }}>
                        <button
                            type="button"
                            className="pc-pr-btn pc-pr-btn--sm"
                            disabled={isSaving}
                            onClick={() => {
                                if (!change) return;
                                METRICS.forEach((metric) => change(nameOf(`settings.dashboard.${metric.name}`), true));
                            }}
                        >Включить все</button>
                    </div>
                </div>
                <div className="cb">
                    <div className="pc-pr-tgrid pc-pr-tgrid--3">
                        {METRICS.map((metric) => (
                            <ToggleRow
                                key={metric.name}
                                name={nameOf(`settings.dashboard.${metric.name}`)}
                                title={metric.title}
                                hint={metric.hint}
                                disabled={isSaving}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="pc-pr-card">
                <div className="ch">
                    <h3>Разрезы статистики</h3>
                    <span className="hint">диаграммы под таблицей</span>
                </div>
                <div className="cb">
                    <div className="pc-pr-tgrid pc-pr-tgrid--3">
                        {STATS.map((stat) => (
                            <ToggleRow
                                key={stat.name}
                                name={nameOf(`settings.dashboard.statistics.${stat.name}`)}
                                title={stat.title}
                                hint={stat.hint}
                                disabled={isSaving}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="pc-pr-card">
                <div className="ch">
                    <h3>Очереди в отчёте</h3>
                    <span className="hint">звонки каких очередей попадают в выборку</span>
                    <StatusChip tone="mono" sx={{ marginLeft: 'auto' }}>
                        {queueCount} из {(queuesList || []).length}
                    </StatusChip>
                </div>
                <div className="cb">
                    <Field
                        name={nameOf('settings.queues')}
                        component={QueuesPicker}
                        queuesList={queuesList}
                        disabled={isSaving}
                    />
                </div>
            </div>
        </div>
    );
};
